import {
  Archive,
  ArrowCounterClockwise,
  ClipboardText,
  Cube,
  DotsThree,
  DownloadSimple,
  FlagBanner,
  FlowArrow,
  ListBullets,
  ListNumbers,
  Notebook,
  SquaresFour,
  Stack,
  Textbox,
  Translate,
  Trash,
} from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { ProductWorkflowDialog } from "@/components/schema/ProductWorkflowDialog";
import { ProductLanguageDialog } from "@/components/translation/ProductLanguageDialog";
import { EmptyState, IconButton, Menu, MenuItem, MenuSeparator, MenuTrigger, RowSkeleton } from "@/components/ui";
import { usePermissions } from "@/components/views/api";
import { useCatalogState } from "@/data/CatalogProvider";
import { useProductItems } from "@/data/items";
import type { Product, Space } from "@/data/types";
import { cx } from "@/lib/cx";
import { ArchiveDialog, ArchivedBanner, DeleteDialog, type LifecycleTarget, toggleArchived } from "./Lifecycle";
import { useProductCommands } from "./productCommands";
import { useQueue } from "./queue";
import { DescriptionField, KeyField, NameField } from "./RenameFields";
import { copyRoadmap, downloadRoadmap } from "./roadmap";

export type ProductSection = "items" | "next-up" | "milestones" | "notes" | "fields" | "views";

/** The product a /p/$productKey page is about, with its space and display name. */
export function useProductPage(productKey: string) {
  const { catalog, isPending } = useCatalogState();
  const product = catalog.productByKey.get(productKey.toUpperCase());
  const space = product ? catalog.spaceById.get(product.spaceId) : undefined;
  const name = product ? (product.isSpaceProduct ? m.space_wide() : product.name) : productKey;
  return { catalog, product, space, name, isPending };
}

/** Cold load: skeleton. Unknown key: the "product not found" empty state. */
export function ProductFallback({ isPending }: { isPending: boolean }) {
  if (isPending) return <RowSkeleton rows={8} className="pt-24" />;
  return (
    <div className="px-5 pt-10 sm:px-8">
      <EmptyState icon={Cube} title={m.product_not_found_title()} description={m.product_not_found_body()} />
    </div>
  );
}

/**
 * Header of every product page: the product name (the page's h1) with its key, actions on the
 * right, the product sections (Items, Next up, Milestones, Notes) as tabs with live counts, and an
 * optional toolbar row (filter bar) below. Sections are links: each has its own URL.
 */
export function ProductHeader({
  product,
  space,
  name,
  section,
  actions,
  toolbar,
  headingLevel = 1,
}: {
  product: Product;
  space: Space | undefined;
  name: string;
  section: ProductSection;
  actions?: ReactNode;
  toolbar?: ReactNode;
  /** 2 when the page's own document carries the h1 (a note). */
  headingLevel?: 1 | 2;
}) {
  const H = headingLevel === 1 ? "h1" : "h2";
  const Icon = product.isSpaceProduct ? SquaresFour : Cube;
  useProductCommands(product, section);
  const perms = usePermissions();
  // A product of an archived space is archived with it: only the space can bring it back.
  const spaceArchived = !!space?.archivedAt;
  const archived = !!product.archivedAt || spaceArchived;
  // Product admins edit name, description and key in place. The space-wide product has no name of its own and its
  // key is its space's (renamed on the space overview).
  const canEdit = perms.product("product.admin", product.id) && !archived && !product.isSpaceProduct;
  const target = { kind: "product", key: product.key } as const;
  return (
    <header className="flex shrink-0 flex-col gap-3 px-5 pt-5 pb-3 sm:px-8">
      {/* Fixed row height: the tabs below never jump between sections whose actions differ in height. */}
      <div className="flex min-h-(--control-h-lg) flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 basis-56 items-center gap-2">
          <Icon size={18} aria-hidden="true" className="shrink-0 text-fg-subtle" />
          {canEdit ? (
            <H className="min-w-0 shrink text-xl leading-7 font-semibold tracking-tight text-fg">
              <NameField
                target={target}
                value={product.name}
                label={m.rename_product_name()}
                className="text-xl leading-7 font-semibold tracking-tight text-fg"
              />
            </H>
          ) : (
            <H className="truncate-title text-xl font-semibold tracking-tight text-fg" title={name}>
              {name}
            </H>
          )}
          {canEdit ? (
            <span className="shrink-0" title={space?.name}>
              <KeyField target={target} productId={product.id} />
            </span>
          ) : (
            <span className="shrink-0 font-mono text-xs text-fg-subtle" title={space?.name}>
              {product.key}
            </span>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
          <ProductMenu product={product} space={space} name={name} />
        </div>
      </div>
      {canEdit ? (
        <div className="-mt-1.5 max-w-3xl">
          <DescriptionField
            target={target}
            value={product.description ?? ""}
            label={m.rename_product_description()}
            className="text-sm leading-5 text-fg-muted"
          />
        </div>
      ) : product.description && !product.isSpaceProduct ? (
        <p className="-mt-1.5 max-w-3xl px-1.5 text-sm leading-5 whitespace-pre-wrap text-fg-muted">
          {product.description}
        </p>
      ) : null}
      {archived ? (
        <ArchivedBanner
          kind={spaceArchived ? "space" : "product"}
          canUnarchive={
            spaceArchived
              ? !!space && perms.space("space.admin", space.id)
              : perms.product("product.admin", product.id) && !product.isSpaceProduct
          }
          onUnarchive={() =>
            spaceArchived && space
              ? toggleArchived({ kind: "space", key: space.key, name: space.name }, false)
              : toggleArchived({ kind: "product", key: product.key, name }, false)
          }
        />
      ) : null}
      <ProductNav product={product} section={section} />
      {toolbar}
    </header>
  );
}

function ProductNav({ product, section }: { product: Product; section: ProductSection }) {
  const items = useProductItems([product.id]);
  const { catalog } = useCatalogState();
  const queue = useQueue(product.id, product.key);
  let open = 0;
  for (const i of items.items) {
    const c = catalog.statusById.get(i.statusId)?.category;
    if (!i.archivedAt && c !== "completed" && c !== "canceled") open++;
  }
  const milestones = catalog.milestones.filter((ms) => ms.productId === product.id && ms.status === "open").length;
  const tabs: Array<{ id: ProductSection; to: string; label: string; icon: typeof Cube; count?: number }> = [
    { id: "items", to: "/p/$productKey", label: m.pnav_items(), icon: ListBullets, count: open },
    {
      id: "next-up",
      to: "/p/$productKey/next-up",
      label: m.nextup_title(),
      icon: ListNumbers,
      count: queue.entries.length,
    },
    { id: "milestones", to: "/p/$productKey/milestones", label: m.ms_title(), icon: FlagBanner, count: milestones },
    { id: "notes", to: "/p/$productKey/notes", label: m.notes_title(), icon: Notebook },
  ];
  // On narrow screens the tab row scrolls sideways: keep the current section in view. Re-checked when tabs change
  // size (counts arrive later), and only ever horizontally, so it never moves the page.
  const navRef = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the current section changes
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const reveal = () => {
      const el = nav.querySelector<HTMLElement>('[aria-current="page"]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      const n = nav.getBoundingClientRect();
      if (r.right > n.right) nav.scrollLeft += r.right - n.right + 8;
      else if (r.left < n.left) nav.scrollLeft -= n.left - r.left + 8;
    };
    const observer = new ResizeObserver(reveal);
    for (const tab of nav.children) observer.observe(tab);
    reveal();
    return () => observer.disconnect();
  }, [section]);
  return (
    <nav
      ref={navRef}
      aria-label={m.pnav_label()}
      className="scroll-thin -mx-1 flex min-w-0 items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-line"
    >
      {tabs.map((t) => {
        const current = t.id === section;
        return (
          <Link
            key={t.id}
            to={t.to}
            params={{ productKey: product.key }}
            // `current` decides (Items also covers list/board/timeline); without `exact` the router would mark Items
            // as current on every product page, since its URL is a prefix of all of them.
            activeOptions={{ exact: true }}
            aria-current={current ? "page" : undefined}
            className={cx(
              "relative inline-flex h-(--control-h-lg) shrink-0 items-center gap-1.5 rounded-control px-2 text-sm font-medium whitespace-nowrap outline-hidden transition-colors duration-(--dur-fast)",
              "focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-focus",
              "after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full",
              current ? "text-fg after:bg-accent" : "text-fg-muted after:bg-transparent hover:text-fg",
            )}
          >
            <t.icon
              size={14}
              weight={current ? "fill" : "regular"}
              aria-hidden="true"
              className={current ? "text-nav-icon-active" : "text-fg-subtle"}
            />
            {t.label}
            {t.count ? <span className="text-xs text-fg-subtle tnum">{t.count > 999 ? "999+" : t.count}</span> : null}
          </Link>
        );
      })}
    </nav>
  );
}

/** Product actions: Fields, manage views, content language (product admins), ROADMAP.md export. */
function ProductMenu({ product, space, name }: { product: Product; space: Space | undefined; name: string }) {
  const productKey = product.key;
  const navigate = useNavigate();
  const canAdmin = usePermissions().product("product.admin", product.id);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // The space-wide product follows its space (archive or delete the space instead); archived means read-only.
  const archived = !!product.archivedAt || !!space?.archivedAt;
  const target: LifecycleTarget = { kind: "product", key: product.key, name, spaceKey: space?.key };
  const ownLifecycle = !product.isSpaceProduct;
  return (
    <>
      <MenuTrigger>
        <IconButton aria-label={m.pnav_more()} icon={DotsThree} size="sm" />
        <Menu aria-label={m.pnav_more()} placement="bottom end">
          <MenuItem
            id="fields"
            icon={Textbox}
            onAction={() => void navigate({ to: "/p/$productKey/fields", params: { productKey } })}
          >
            {m.fields_title()}
          </MenuItem>
          <MenuItem
            id="views"
            icon={Stack}
            onAction={() => void navigate({ to: "/p/$productKey/views", params: { productKey } })}
          >
            {m.views_manage()}
          </MenuItem>
          <MenuItem
            id="trash"
            icon={Trash}
            onAction={() => void navigate({ to: "/p/$productKey/trash", params: { productKey } })}
          >
            {m.trash_title()}
          </MenuItem>
          {canAdmin && !archived ? (
            <MenuItem id="content-language" icon={Translate} onAction={() => setLanguageOpen(true)}>
              {m.cl_menu()}
            </MenuItem>
          ) : null}
          {canAdmin && !archived ? (
            <MenuItem id="workflow" icon={FlowArrow} onAction={() => setWorkflowOpen(true)}>
              {m.pwf_menu()}
            </MenuItem>
          ) : null}
          <MenuSeparator />
          <MenuItem id="copy" icon={ClipboardText} onAction={() => void copyRoadmap(productKey)}>
            {m.roadmap_copy()}
          </MenuItem>
          <MenuItem id="download" icon={DownloadSimple} onAction={() => void downloadRoadmap(productKey)}>
            {m.roadmap_download()}
          </MenuItem>
          {canAdmin && ownLifecycle && !space?.archivedAt ? <MenuSeparator /> : null}
          {canAdmin && ownLifecycle && !space?.archivedAt ? (
            product.archivedAt ? (
              <MenuItem id="unarchive" icon={ArrowCounterClockwise} onAction={() => void toggleArchived(target, false)}>
                {m.lifecycle_unarchive_product()}
              </MenuItem>
            ) : (
              <MenuItem id="archive" icon={Archive} onAction={() => setArchiveOpen(true)}>
                {m.lifecycle_archive_product()}
              </MenuItem>
            )
          ) : null}
          {space?.canDelete && ownLifecycle ? (
            <MenuItem id="delete" icon={Trash} tone="danger" onAction={() => setDeleteOpen(true)}>
              {m.lifecycle_delete_product()}
            </MenuItem>
          ) : null}
        </Menu>
      </MenuTrigger>
      {archiveOpen ? <ArchiveDialog target={target} isOpen={archiveOpen} onOpenChange={setArchiveOpen} /> : null}
      {deleteOpen ? <DeleteDialog target={target} isOpen={deleteOpen} onOpenChange={setDeleteOpen} /> : null}
      <ProductLanguageDialog product={product} name={name} isOpen={languageOpen} onOpenChange={setLanguageOpen} />
      {workflowOpen ? (
        <ProductWorkflowDialog
          productKey={product.key}
          productName={name}
          isOpen={workflowOpen}
          onOpenChange={setWorkflowOpen}
        />
      ) : null}
    </>
  );
}
